import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, Pencil, Phone, Mail, Share2, ShieldCheck, Users } from "lucide-react";
import { maskPhoneInput } from "@/lib/dateTimeMask";
import { toTitleCasePt } from "@/lib/format";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useAuth } from "@/context/AuthContext";

// Lote 160 — Contatos: parceiros externos (correspondentes, apoio sem login). Cadastrar NÃO dá acesso a nada.
// Equipe interna mora em Escritório. Cadastros antigos pedem uma revisão (classificar) — nada é apagado nem ampliado sozinho.
const EMPTY = { name: "", role: "", phone: "", email: "", oab: "", uf: "", obs: "", classificacao: "externo" };
const FUNCOES = ["Correspondente", "Advogado parceiro", "Estagiário", "Secretária", "Perito", "Outro"];
const CLASSES = [["externo", "Parceiro externo"], ["sem_acesso", "Contato sem acesso"], ["interno", "Integrante interno"]];
export const ROTULO_CLASSE = Object.fromEntries(CLASSES);
const detalheDe = (e) => e?.response?.data?.detail;

export default function ColabContatos({ onCompartilhar, onMudou }) {
  const { user } = useAuth() || {};
  const noEscritorio = !!user?.escritorio;
  const [lista, setLista] = useState(null);
  const [erro, setErro] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirma, setConfirma] = useState(null);   // { tipo: "excluir"|"classificar", contato, vinculos, classe }

  const load = async () => {
    try { setLista((await http.get("/equipe")).data); setErro(false); } catch { setErro(true); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  const abrirNovo = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const abrirEditar = (m) => {
    setEditing(m.member_id);
    setForm({ name: m.name || "", role: m.role || "", phone: m.phone || "", email: m.email || "", oab: m.oab || "", uf: m.uf || "", obs: m.obs || "", classificacao: m.classificacao || "externo" });
    setOpen(true);
  };
  const salvar = async () => {
    setSaving(true);
    try {
      const corpo = { ...form };
      if (editing) await http.put(`/equipe/${editing}`, corpo); else await http.post("/equipe", corpo);
      setOpen(false); toast.success(editing ? "Contato atualizado" : "Contato cadastrado — sem acesso a nenhum dado");
      await load(); onMudou?.();
    } catch (e) { toast.error(typeof detalheDe(e) === "string" ? detalheDe(e) : "Não consegui salvar"); } finally { setSaving(false); }
  };

  const excluir = async (m, encerrar = false) => {
    try {
      const r = await http.delete(`/equipe/${m.member_id}`, { params: encerrar ? { encerrar_vinculos: true } : undefined });
      setConfirma(null);
      toast.success(r.data.acessos_encerrados ? `Contato excluído e ${r.data.acessos_encerrados} acesso(s) encerrado(s)` : "Contato excluído");
      await load(); onMudou?.();
    } catch (e) {
      const d = detalheDe(e);
      if (e?.response?.status === 409 && d?.vinculos) setConfirma({ tipo: "excluir", contato: m, vinculos: d.vinculos });
      else toast.error(typeof d === "string" ? d : "Não consegui excluir");
    }
  };
  const classificar = async (m, classe, encerrar = false) => {
    try {
      await http.put(`/equipe/${m.member_id}/classificacao`, { classificacao: classe }, { params: encerrar ? { encerrar_vinculos: true } : undefined });
      setConfirma(null); toast.success(`Classificado: ${ROTULO_CLASSE[classe]}`);
      await load(); onMudou?.();
    } catch (e) {
      const d = detalheDe(e);
      if (e?.response?.status === 409 && d?.vinculos) setConfirma({ tipo: "classificar", contato: m, vinculos: d.vinculos, classe });
      else toast.error(typeof d === "string" ? d : "Não consegui classificar");
    }
  };

  const nRevisar = (lista || []).filter((m) => m.precisa_revisar).length;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="aba-contatos">
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-xs text-stone-500 leading-relaxed">
          Parceiros externos ao escritório. <b className="font-semibold text-stone-700">Cadastrar um contato não concede acesso a nenhum dado.</b>
          {noEscritorio && <> A equipe interna fica em <Link to="/escritorio" className="underline">Escritório → Integrantes</Link>.</>}
        </p>
        <Button type="button" size="sm" variant="outline" data-testid="contato-novo" onClick={abrirNovo} className="gap-1.5 border-stone-300 shrink-0">
          <UserPlus className="w-4 h-4" strokeWidth={1.75} /> Novo contato
        </Button>
      </div>

      {nRevisar > 0 && (
        <div data-testid="contatos-revisar" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 flex gap-2">
          <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.75} />
          <span><b>{nRevisar} {nRevisar === 1 ? "cadastro antigo precisa" : "cadastros antigos precisam"} de revisão.</b> Escolha, em cada um, se é parceiro externo, contato sem acesso ou integrante interno. Nada foi alterado automaticamente e nenhum deles tem acesso a dados.</span>
        </div>
      )}

      {erro ? (
        <div data-testid="contatos-erro" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
          Não foi possível carregar os contatos. <button type="button" className="underline font-semibold" onClick={load}>Tentar de novo</button>
        </div>
      ) : lista === null ? (
        <div className="p-8 text-center text-stone-500">Carregando…</div>
      ) : lista.length === 0 ? (
        <div data-testid="contatos-vazio" className="bg-white border border-stone-200 rounded-md p-10 text-center">
          <Users className="w-10 h-10 mx-auto text-stone-300 mb-3" strokeWidth={1.25} />
          <p className="font-display text-lg text-stone-900 mb-1">Nenhum contato</p>
          <p className="text-sm text-stone-500">Cadastre correspondentes e parceiros para compartilhar eventos com eles.</p>
        </div>
      ) : (
        <ul className="space-y-2.5">
          {lista.map((m) => (
            <li key={m.member_id} data-testid={`contato-${m.member_id}`} className="bg-white border border-stone-200 rounded-md px-4 py-3.5">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg font-semibold text-stone-900 leading-snug">{toTitleCasePt(m.name)}</div>
                  <div className="text-xs text-stone-500">
                    {[m.role, m.oab ? `OAB ${m.oab}${m.uf ? "/" + m.uf : ""}` : null].filter(Boolean).join(" · ")}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-stone-600">
                    {m.phone && <span className="inline-flex items-center gap-1.5 tabular-nums"><Phone className="w-3.5 h-3.5 text-stone-400" strokeWidth={1.5} />{m.phone}</span>}
                    {m.email && <span className="inline-flex items-center gap-1.5 break-all"><Mail className="w-3.5 h-3.5 text-stone-400" strokeWidth={1.5} />{m.email}</span>}
                  </div>
                  {m.obs && <div className="mt-1 text-xs text-stone-500">Obs. interna: {m.obs}</div>}
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <span data-testid={`contato-classe-${m.member_id}`} className="inline-flex px-2 py-0.5 rounded-full border border-stone-200 bg-stone-100 text-[10px] font-bold uppercase tracking-wide text-stone-600">
                      {m.classificacao ? ROTULO_CLASSE[m.classificacao] : "Precisa de revisão"}
                    </span>
                    {m.iapauta && <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-gold-500 text-black border border-gold-600">Usa o IApauta</span>}
                    {m.vinculos_vivos > 0 && <span data-testid={`contato-vinculos-${m.member_id}`} className="inline-flex px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-[10px] font-bold uppercase tracking-wide text-emerald-800">{m.vinculos_vivos} em aberto</span>}
                  </div>
                  {m.no_escritorio && (
                    <div data-testid={`contato-no-escritorio-${m.member_id}`} className="mt-1.5 text-xs text-stone-600">
                      Este e-mail é de um integrante do escritório. A equipe interna é gerenciada em Escritório.
                    </div>
                  )}
                  {m.precisa_revisar && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`contato-revisar-${m.member_id}`}>
                      {CLASSES.map(([v, r]) => (
                        <Button key={v} type="button" size="sm" variant="outline" data-testid={`classificar-${m.member_id}-${v}`} onClick={() => classificar(m, v)}
                          className={`h-8 text-xs border-stone-300 ${m.no_escritorio && v === "interno" ? "border-stone-500 font-semibold" : ""}`}>{r}</Button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center shrink-0">
                  {m.classificacao === "externo" && (
                    <Button type="button" size="sm" variant="outline" data-testid={`contato-compartilhar-${m.member_id}`} onClick={() => onCompartilhar?.(m.member_id)} className="gap-1.5 border-stone-300 mr-1">
                      <Share2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Compartilhar
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" aria-label="Editar" data-testid={`contato-editar-${m.member_id}`} onClick={() => abrirEditar(m)} className="text-stone-500"><Pencil className="w-4 h-4" strokeWidth={1.75} /></Button>
                  <Button variant="ghost" size="sm" aria-label="Excluir" data-testid={`contato-excluir-${m.member_id}`} onClick={() => excluir(m)} className="text-rose-brand hover:bg-rose-100 hover:text-rose-700"><Trash2 className="w-4 h-4" strokeWidth={1.75} /></Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contato-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Editar contato" : "Novo contato"}</DialogTitle>
            <DialogDescription>Cadastrar não concede acesso a nenhum dado. O acesso só existe depois de um compartilhamento aceito.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label htmlFor="c-nome">Nome</Label><Input id="c-nome" data-testid="contato-nome" value={form.name} onChange={set("name")} /></div>
            <div>
              <Label htmlFor="c-funcao">Função</Label>
              <Input id="c-funcao" list="funcoes-contato" data-testid="contato-funcao" value={form.role} onChange={set("role")} placeholder="Correspondente, estagiário…" />
              <datalist id="funcoes-contato">{FUNCOES.map((f) => <option key={f} value={f} />)}</datalist>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label htmlFor="c-email">E-mail</Label><Input id="c-email" type="email" data-testid="contato-email" value={form.email} onChange={set("email")} /></div>
              <div><Label htmlFor="c-tel">Telefone (opcional)</Label><Input id="c-tel" data-testid="contato-telefone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: maskPhoneInput(e.target.value) }))} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label htmlFor="c-oab">OAB (opcional)</Label><Input id="c-oab" data-testid="contato-oab" value={form.oab} onChange={set("oab")} /></div>
              <div><Label htmlFor="c-uf">UF</Label><Input id="c-uf" maxLength={2} data-testid="contato-uf" value={form.uf} onChange={(e) => setForm((f) => ({ ...f, uf: e.target.value.toUpperCase() }))} /></div>
            </div>
            <div>
              <Label htmlFor="c-classe">Classificação</Label>
              <select id="c-classe" data-testid="contato-classe" value={form.classificacao} onChange={set("classificacao")} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                {CLASSES.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
              </select>
            </div>
            <div><Label htmlFor="c-obs">Observações internas</Label><Textarea id="c-obs" data-testid="contato-obs" rows={2} value={form.obs} onChange={set("obs")} placeholder="Só você vê. Nunca vai ao parceiro." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="contato-salvar" disabled={saving || !form.name.trim()} onClick={salvar} className="bg-stone-900 text-white hover:bg-stone-800">{saving ? "Salvando…" : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirma} onOpenChange={(v) => !v && setConfirma(null)}>
        <DialogContent className="max-w-md" data-testid="contato-confirma">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Este contato tem acessos em aberto</DialogTitle>
            <DialogDescription>
              {confirma ? `${toTitleCasePt(confirma.contato.name)} tem ${confirma.vinculos.length} ${confirma.vinculos.length === 1 ? "compartilhamento ativo ou pendente" : "compartilhamentos ativos ou pendentes"}. ` : ""}
              Para {confirma?.tipo === "excluir" ? "excluir o contato" : "mudar a classificação"}, esses acessos precisam ser encerrados de forma explícita. O histórico dos compartilhamentos é mantido.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirma(null)}>Manter como está</Button>
            <Button data-testid="contato-confirma-encerrar" onClick={() => (confirma.tipo === "excluir" ? excluir(confirma.contato, true) : classificar(confirma.contato, confirma.classe, true))}
              className="bg-rose-700 text-white hover:bg-rose-800">Encerrar acessos e continuar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
